/**
 * Passos 5 e 6: o relatorio e a politica de RLS. Sao os dois passos que decidem QUEM VE O QUE.
 *
 * Duas coisas aqui erram em silencio, e as duas estao explicadas na tela:
 *
 * - **Workspace do Auth e porta de acesso.** O relatorio pode existir, estar publicado e correto, e
 *   ainda assim ninguem ver - porque nenhum profile alcanca o workspace escolhido.
 * - **RLS e um filtro DAX com nome.** A `role` digitada precisa existir, com o mesmo nome, no
 *   semantic model. Nome errado nao da erro na criacao: da zero linha na hora de abrir.
 */

import { Button } from '@ledger-glass/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'

import { fetchConnections, fetchDatasets, fetchHospitals, fetchReports } from '../api/catalog'
import {
  criarDataset,
  criarPolicy,
  criarReport,
  descobrirReport,
  extrairDoLink,
  type Descoberta,
} from '../api/onboarding'
import { ESTADOS_DE_PUBLICACAO, RESOURCE_DISCOVERY } from '../api/types'
import { usePermissoes } from '../auth/permissoes'
import { Alert } from '../components/Alert'
import { humanizar } from '../errors/humanizar'
import { CampoSelecao, CampoTexto } from './campos'
import type { PassoProps } from './PassosCadastro'

// ------------------------------------------------------------------ 5. report

const REPORT_VAZIO = {
  name: '',
  category: '',
  description: '',
  powerbi_workspace_id: '',
  powerbi_report_id: '',
  embed_url: '',
}

export function PassoReport({ snapshot, cliente }: PassoProps) {
  const id = useId()
  const { pode, carregando: permissoesCarregando } = usePermissoes()
  const queryClient = useQueryClient()
  const conexoes = useQuery({ queryKey: ['powerbi-connections'], queryFn: fetchConnections })
  const datasets = useQuery({ queryKey: ['datasets'], queryFn: fetchDatasets })
  const hospitais = useQuery({ queryKey: ['hospitals'], queryFn: fetchHospitals })

  const [hospital, setHospital] = useState('')
  const [conexao, setConexao] = useState('')
  const [link, setLink] = useState('')
  const [workspace, setWorkspace] = useState('')
  const [dataset, setDataset] = useState('')
  const [publicacao, setPublicacao] = useState<string>('draft')
  const [campos, setCampos] = useState(REPORT_VAZIO)
  const [buscaOffline, setBuscaOffline] = useState(false)

  const doCliente = {
    conexoes: (conexoes.data ?? []).filter((item) => item.client_id === cliente?.id),
    datasets: (datasets.data ?? []).filter((item) => item.client_id === cliente?.id),
    hospitais: (hospitais.data ?? []).filter((item) => item.client_id === cliente?.id),
  }

  /** Preenche o formulario com o que veio do Power BI (ou do link) e vincula o dataset. */
  function aplicar(descoberta: Descoberta, datasetLocal: string | undefined) {
    setCampos((atual) => ({
      ...atual,
      name: descoberta.name ?? atual.name,
      powerbi_workspace_id: descoberta.powerbi_workspace_id ?? atual.powerbi_workspace_id,
      powerbi_report_id: descoberta.powerbi_report_id ?? atual.powerbi_report_id,
      embed_url: descoberta.embed_url ?? atual.embed_url,
    }))
    if (datasetLocal !== undefined) setDataset(datasetLocal)
  }

  /**
   * A conexao EFETIVA, nao a escolhida: com uma conexao so, o `select` ja mostra ela mas o estado
   * comeca vazio. Usar `conexao` cru aqui fazia a busca reclamar de um campo que a tela mostrava
   * preenchido, e a criacao mandar conexao vazia.
   */
  const conexaoEfetiva = conexao === '' ? (doCliente.conexoes[0]?.id ?? '') : conexao

  const busca = useMutation({
    mutationFn: async () => {
      const conexaoEscolhida = doCliente.conexoes.find((item) => item.id === conexaoEfetiva)
      if (conexaoEscolhida === undefined) throw new Error('Escolha a conexão Power BI.')
      const descoberta = await descobrirReport(conexaoEscolhida.connection_key, link)
      if (descoberta === undefined) throw new Error('O Power BI não devolveu o relatório.')

      // Dataset: reaproveita o que ja existe com o mesmo id do PowerBI; senao cria. Sem isso,
      // cada busca criaria um dataset duplicado apontando para o mesmo semantic model.
      const existente = doCliente.datasets.find(
        (item) => item.powerbi_dataset_id === descoberta.powerbi_dataset_id,
      )
      let local = existente?.id
      if (local === undefined && descoberta.powerbi_dataset_id != null) {
        const criado = await criarDataset({
          client_id: cliente?.id ?? '',
          powerbi_connection_id: conexaoEscolhida.id,
          name: `${descoberta.name ?? 'Dataset'} - dataset`,
          powerbi_workspace_id: descoberta.powerbi_workspace_id ?? '',
          powerbi_dataset_id: descoberta.powerbi_dataset_id,
        })
        local = criado?.id
        await queryClient.invalidateQueries({ queryKey: ['datasets'] })
        await queryClient.invalidateQueries({ queryKey: ['summary'] })
      }
      return { descoberta, local }
    },
    onSuccess: ({ descoberta, local }) => {
      setBuscaOffline(false)
      aplicar(descoberta, local)
    },
  })

  const extracao = useMutation({
    mutationFn: () => extrairDoLink(link),
    onSuccess: (descoberta) => {
      setBuscaOffline(true)
      if (descoberta !== undefined) aplicar(descoberta, undefined)
    },
  })

  const criacao = useMutation({
    mutationFn: () => {
      const escolhido = doCliente.datasets.find((item) => item.id === dataset)
      return criarReport({
        client_id: cliente?.id ?? '',
        hospital_id: hospital === '' ? null : hospital,
        powerbi_connection_id: escolhido?.powerbi_connection_id ?? conexaoEfetiva,
        dataset_id: escolhido?.id ?? null,
        auth_workspace_id: workspace,
        report_type: 'pbi',
        name: campos.name,
        category: campos.category === '' ? null : campos.category,
        description: campos.description === '' ? null : campos.description,
        powerbi_workspace_id: campos.powerbi_workspace_id === '' ? null : campos.powerbi_workspace_id,
        powerbi_report_id: campos.powerbi_report_id === '' ? null : campos.powerbi_report_id,
        embed_url: campos.embed_url === '' ? null : campos.embed_url,
        publication_status: publicacao,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['reports'] })
      await queryClient.invalidateQueries({ queryKey: ['summary'] })
      setCampos(REPORT_VAZIO)
      setLink('')
    },
  })

  if (cliente === undefined) {
    return <Alert tone="warning">Faça o passo 1 antes: o tenant ainda não é cliente.</Alert>
  }
  if (doCliente.conexoes.length === 0) {
    return <Alert tone="warning">Este cliente ainda não tem conexão Power BI. Faça o passo 3.</Alert>
  }

  function muda(chave: keyof typeof REPORT_VAZIO) {
    return (valor: string) => setCampos((atual) => ({ ...atual, [chave]: valor }))
  }

  const organizacao =
    hospital === ''
      ? null
      : (doCliente.hospitais.find((item) => item.id === hospital)?.auth_organization_id ?? null)
  const workspaces = snapshot.workspaces.filter(
    (item) => organizacao === null || item.organization_id === organizacao,
  )
  const completo = workspace !== '' && campos.name.trim() !== ''

  return (
    <>
      <p className="pagina__descricao">
        O relatório em si, ligado a <strong>um workspace do Auth</strong> — e esse workspace é a
        porta de acesso: quem não o alcança pelo profile não vê o relatório, mesmo publicado.
      </p>

      <section className="painel">
        <h3 className="painel__titulo">Buscar no Power BI</h3>
        <p className="pagina__descricao">
          Cole o link do relatório e busque: o sistema consulta a API do Power BI e traz o
          <strong> embed URL e o dataset id reais</strong>, criando o dataset se ainda não existir. A
          extração offline só lê o link, sem validar nada — o embed montado assim pode estar errado, e
          o erro só aparece quando o usuário final abre o relatório.
        </p>
        <div className="formulario">
          <CampoSelecao
            aoMudar={setConexao}
            id={`${id}-conn`}
            opcoes={doCliente.conexoes.map((item) => ({ valor: item.id, rotulo: item.name }))}
            rotulo="Conexão Power BI"
            valor={conexaoEfetiva}
          />
          <CampoTexto
            aoMudar={setLink}
            exemplo="https://app.powerbi.com/groups/.../reports/..."
            id={`${id}-link`}
            rotulo="Link do Power BI"
            valor={link}
          />
          <div className="botoes">
            {/* Falar com a API da Microsoft usando o service principal guardado e capacidade
                propria (spec 0009): quem pode cadastrar relatorio nao necessariamente pode sair
                consultando o Power BI do cliente. */}
            {permissoesCarregando || pode(RESOURCE_DISCOVERY, 'manage') ? (
              <Button
                disabled={link.trim() === '' || busca.isPending}
                onClick={() => busca.mutate()}
                type="button"
              >
                {busca.isPending ? 'Buscando…' : 'Buscar no Power BI'}
              </Button>
            ) : null}
            <Button
              disabled={link.trim() === '' || extracao.isPending}
              onClick={() => extracao.mutate()}
              type="button"
              variant="ghost"
            >
              Extrair offline (só do link)
            </Button>
          </div>
          {busca.error === null ? null : <Alert tone="danger">{humanizar(busca.error)}</Alert>}
          {extracao.error === null ? null : <Alert tone="danger">{humanizar(extracao.error)}</Alert>}
          {busca.isSuccess ? (
            <Alert tone="positive">
              Encontrado no Power BI: <strong>{busca.data.descoberta.name}</strong> ·{' '}
              {busca.data.descoberta.pages?.length ?? 0} página(s). Dataset criado ou vinculado.
            </Alert>
          ) : null}
          {buscaOffline ? (
            <Alert tone="warning">
              Extraído do link, <strong>sem validar no Power BI</strong>. Confira o embed URL antes de
              publicar.
            </Alert>
          ) : null}
        </div>
      </section>

      <form
        className="formulario"
        onSubmit={(evento) => {
          evento.preventDefault()
          if (completo) criacao.mutate()
        }}
      >
        <CampoSelecao
          aoMudar={setHospital}
          dica="Opcional. Escolher um hospital filtra os workspaces por aquela organization."
          id={`${id}-hosp`}
          opcoes={doCliente.hospitais.map((item) => ({ valor: item.id, rotulo: item.name }))}
          rotulo="Hospital (opcional)"
          valor={hospital}
          vazio="— nenhum —"
        />
        <CampoSelecao
          aoMudar={setWorkspace}
          dica="Porta de acesso: só quem alcança este workspace pelo profile vê o relatório."
          id={`${id}-ws`}
          obrigatorio
          opcoes={workspaces.map((item) => ({ valor: item.id, rotulo: item.name }))}
          rotulo="Workspace (Auth)"
          valor={workspace}
          vazio="— escolha —"
        />
        <CampoSelecao
          aoMudar={setDataset}
          dica="O semantic model do relatório. Necessário para ter RLS."
          id={`${id}-ds`}
          opcoes={doCliente.datasets.map((item) => ({ valor: item.id, rotulo: item.name }))}
          rotulo="Dataset"
          valor={dataset}
          vazio="— nenhum —"
        />
        <CampoTexto
          aoMudar={muda('name')}
          dica="Nome genérico: o mesmo relatório costuma servir vários clientes."
          exemplo="Comercial"
          id={`${id}-nome`}
          obrigatorio
          rotulo="Nome do relatório"
          valor={campos.name}
        />
        <CampoTexto
          aoMudar={muda('category')}
          id={`${id}-cat`}
          rotulo="Categoria (opcional)"
          valor={campos.category}
        />
        <CampoTexto
          aoMudar={muda('description')}
          id={`${id}-desc`}
          rotulo="Descrição (opcional)"
          valor={campos.description}
        />
        <CampoTexto
          aoMudar={muda('powerbi_workspace_id')}
          id={`${id}-pbiws`}
          rotulo="PowerBI Workspace ID (groupId)"
          valor={campos.powerbi_workspace_id}
        />
        <CampoTexto
          aoMudar={muda('powerbi_report_id')}
          id={`${id}-pbiid`}
          rotulo="PowerBI Report ID"
          valor={campos.powerbi_report_id}
        />
        <CampoTexto
          aoMudar={muda('embed_url')}
          dica="Obrigatório para publicar. É com ela que o Viewer renderiza."
          id={`${id}-embed`}
          rotulo="Embed URL"
          valor={campos.embed_url}
        />
        <CampoSelecao
          aoMudar={setPublicacao}
          dica="Só 'published' aparece no Report Viewer."
          id={`${id}-pub`}
          opcoes={ESTADOS_DE_PUBLICACAO.map((estado) => ({ valor: estado, rotulo: estado }))}
          rotulo="Publicação"
          valor={publicacao}
        />

        {criacao.error === null ? null : <Alert tone="danger">{humanizar(criacao.error)}</Alert>}
        {criacao.isSuccess ? (
          <Alert tone="positive">Relatório criado. Configure a política de RLS no passo 6.</Alert>
        ) : null}
        <div>
          <Button disabled={!completo || criacao.isPending} type="submit">
            {criacao.isPending ? 'Criando…' : 'Criar relatório'}
          </Button>
        </div>
      </form>
    </>
  )
}

// ------------------------------------------------------------------ 6. policy

/** Fontes de identidade aceitas (specs 0004/0005). A ordem define o padrao do formulario. */
const FONTES: Array<{ valor: string; rotulo: string }> = [
  { valor: 'email', rotulo: 'E-mail do usuário (recomendado)' },
  { valor: 'user_id', rotulo: 'user_id do Auth (GUID)' },
  { valor: 'organization_id', rotulo: 'id da organização do usuário' },
  { valor: 'organization_slug', rotulo: 'slug da organização do usuário' },
]

export function PassoPolicy({ snapshot, cliente }: PassoProps) {
  const id = useId()
  const queryClient = useQueryClient()
  const relatorios = useQuery({ queryKey: ['reports'], queryFn: fetchReports })

  const [reportId, setReportId] = useState('')
  const [profileId, setProfileId] = useState('')
  const [nome, setNome] = useState('')
  const [descricao, setDescricao] = useState('')
  const [identidade, setIdentidade] = useState('email')
  const [customData, setCustomData] = useState('')
  const [roles, setRoles] = useState('')

  const doCliente = (relatorios.data ?? []).filter(
    (item) => item.client_id === cliente?.id && item.dataset_id !== null,
  )
  const report = doCliente.find((item) => item.id === reportId)

  const criacao = useMutation({
    mutationFn: () =>
      criarPolicy({
        client_id: cliente?.id ?? '',
        report_id: reportId,
        auth_profile_id: profileId,
        name: nome === '' ? `RLS - ${report?.name ?? ''}` : nome,
        description: descricao === '' ? null : descricao,
        policy_type: 'rls',
        identity_source: identidade,
        custom_data_source: customData === '' ? null : customData,
        rules: roles
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== '')
          .map((role) => ({ role_name: role })),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['summary'] })
      setRoles('')
    },
  })

  if (cliente === undefined) {
    return <Alert tone="warning">Faça o passo 1 antes: o tenant ainda não é cliente.</Alert>
  }
  if (doCliente.length === 0) {
    return (
      <Alert tone="warning">
        Nenhum relatório deste cliente está ligado a um dataset. RLS acontece no semantic model:
        sem dataset, não há o que filtrar. Faça o passo 5.
      </Alert>
    )
  }

  /**
   * Profile sem workspace declarado aparece: o Auth trata ausencia como "alcanca tudo", e esconder
   * esses profiles deixaria a lista vazia sem explicar por que.
   */
  const profiles = snapshot.profiles.filter(
    (item) =>
      item.workspace_ids === undefined ||
      item.workspace_ids.length === 0 ||
      (report !== undefined && item.workspace_ids.includes(report.auth_workspace_id)),
  )

  const completo = reportId !== '' && profileId !== '' && roles.trim() !== ''

  return (
    <>
      <p className="pagina__descricao">
        A política liga <strong>relatório + profile + role(s)</strong> do semantic model. Sem ela, o
        Viewer não gera token de embed para quem depende de RLS.
      </p>
      <Alert tone="info">
        Uma <strong>role</strong> do Power BI é um filtro DAX com nome, definido no modelo (Power BI
        Desktop → Modeling → Manage roles). O nome tem que bater exatamente: errado não dá erro aqui,
        dá zero linha na hora de abrir o relatório.
      </Alert>

      <form
        className="formulario"
        onSubmit={(evento) => {
          evento.preventDefault()
          if (completo) criacao.mutate()
        }}
      >
        <CampoSelecao
          aoMudar={(valor) => {
            setReportId(valor)
            setProfileId('')
          }}
          id={`${id}-report`}
          obrigatorio
          opcoes={doCliente.map((item) => ({
            valor: item.id,
            rotulo: `${item.name} (${item.publication_status})`,
          }))}
          rotulo="Relatório"
          valor={reportId}
          vazio="— escolha —"
        />
        {report !== undefined && profiles.length === 0 ? (
          <Alert tone="warning">
            Nenhum profile do Auth alcança o workspace deste relatório. O vínculo profile ↔ workspace
            é feito no Auth Manager.
          </Alert>
        ) : (
          <CampoSelecao
            aoMudar={setProfileId}
            dica="Só os profiles que alcançam o workspace do relatório."
            id={`${id}-profile`}
            obrigatorio
            opcoes={profiles.map((item) => ({
              valor: item.id,
              rotulo: item.profile_code === undefined ? item.name : `${item.name} (${item.profile_code})`,
            }))}
            rotulo="Profile (Auth)"
            valor={profileId}
            vazio="— escolha —"
          />
        )}
        <CampoTexto
          aoMudar={setNome}
          exemplo={report === undefined ? 'RLS - relatório - profile' : `RLS - ${report.name}`}
          id={`${id}-nome`}
          rotulo="Nome da política"
          valor={nome}
        />
        <CampoTexto
          aoMudar={setDescricao}
          id={`${id}-desc`}
          rotulo="Descrição (opcional)"
          valor={descricao}
        />
        <CampoSelecao
          aoMudar={setIdentidade}
          dica="É o valor enviado como username no token, comparado com USERNAME() no DAX. E-mail é o padrão: o GUID do Auth muda se os usuários forem recriados, e aí o usuário passa a ver zero linhas sem erro nenhum."
          id={`${id}-identidade`}
          obrigatorio
          opcoes={FONTES}
          rotulo="Identidade enviada ao Power BI"
          valor={identidade}
        />
        <CampoSelecao
          aoMudar={setCustomData}
          dica="Valor extra lido por CUSTOMDATA() no DAX. Só preencha se o modelo do cliente usar."
          id={`${id}-custom`}
          opcoes={FONTES}
          rotulo="customData (opcional)"
          valor={customData}
          vazio="— não enviar —"
        />
        <CampoTexto
          aoMudar={setRoles}
          dica="Nome exato da role no semantic model. Várias, separadas por vírgula, são aplicadas juntas."
          exemplo="ReportViewerRLS  ou  RoleA, RoleB"
          id={`${id}-roles`}
          obrigatorio
          rotulo="Role(s) de RLS do dataset"
          valor={roles}
        />

        {criacao.error === null ? null : <Alert tone="danger">{humanizar(criacao.error)}</Alert>}
        {criacao.isSuccess ? <Alert tone="positive">Política criada.</Alert> : null}
        <div>
          <Button disabled={!completo || criacao.isPending} type="submit">
            {criacao.isPending ? 'Criando…' : 'Criar política'}
          </Button>
        </div>
      </form>
    </>
  )
}
